import {
  type RepositoryAccessConfig,
  RepositoryAccessVault,
  VAULT_TYPES,
} from "@p0security/integrations-github-shared";
import { otherInstalledIntegration } from "@p0security/integrations-installer";

import type { ConnectorCloud } from "./iac.ts";

type VaultType = RepositoryAccessConfig["vault"]["type"];

/** Creates the provider that lists P0's installs of `integration`'s
 * iam-write component: the accounts that a field placing the connector, or
 * its vault, can name. */
const newIamWriteInstallsProvider = (integration: string) =>
  otherInstalledIntegration({ integration, component: "iam-write" });

/** Lists the installs that a field placing the connector, or its vault, can
 * name. */
type InstallOptionProvider = ReturnType<typeof newIamWriteInstallsProvider>;

/** A field naming one of P0's installs, and where P0 lists them. */
type InstallFieldSpec = Readonly<{
  description: string;
  installs: InstallOptionProvider;
}>;

/** The cloud's part in the install: what its connector and vault are called,
 * and where P0 lists the installs that place them. The connector reads the
 * key from its own cloud's secret manager, so one cloud decides both. */
export type CloudDispatch = Readonly<{
  /** The cloud, as the region fields name it */
  label: string;
  /** What the connector runs as */
  runtime: string;
  /** Where to apply the connector's Terraform, or run its shell script, in
   * this cloud */
  deployNote: string;
  regionPlaceholder: string;
  hosting: InstallFieldSpec;
  vault: Readonly<{
    install: InstallFieldSpec;
    /** The secret manager, as its provider names it */
    label: string;
    /** The private key secret field's copy, in the secret manager's words
     * for a secret's name */
    secretDescription: string;
  }>;
}>;

const awsCloud: CloudDispatch = {
  label: "AWS",
  runtime: "Lambda function",
  deployNote:
    "Run either where Docker and the AWS CLI are installed, with the AWS CLI signed in to the connector's account. Both copy P0's connector image into ECR.",
  regionPlaceholder: "us-west-2",
  hosting: {
    description:
      "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
    installs: newIamWriteInstallsProvider("aws"),
  },
  vault: {
    install: {
      description:
        "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
      installs: newIamWriteInstallsProvider("aws-sm"),
    },
    label: "AWS Secrets Manager",
    secretDescription:
      "The name, not the ARN, of the AWS Secrets Manager secret that holds, or will hold, the GitHub App's private key, in the account and region above.",
  },
};

const gcpCloud: CloudDispatch = {
  label: "Google Cloud",
  runtime: "Cloud Run service",
  deployNote:
    "Run either as someone who can enable APIs and manage Cloud Run, service accounts and IAM in the connector's project, and IAM on the secret: the Terraform with Google credentials, or the script with the gcloud CLI signed in. P0 looks the service up when you click Next, so deploy it first.",
  regionPlaceholder: "us-central1",
  hosting: {
    description:
      "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
    installs: newIamWriteInstallsProvider("gcloud"),
  },
  vault: {
    install: {
      description:
        "The Google Cloud project the secret is in. P0 lists the projects where its Google Secret Manager integration is installed.",
      installs: newIamWriteInstallsProvider("gcp-sm"),
    },
    label: "Google Secret Manager",
    secretDescription:
      "The name of the Google Secret Manager secret that holds, or will hold, the GitHub App's private key, in the project above. Use only letters, digits, hyphens and underscores. Enter the name, not the resource name projects/<project>/secrets/<name>.",
  },
};

/** Each cloud's part in the install. */
export const CLOUDS: Readonly<Record<ConnectorCloud, CloudDispatch>> = {
  aws: awsCloud,
  gcp: gcpCloud,
};

// The cloud each vault's secret manager is in, by the vault each hosting reads
// its key from.
const VAULT_CLOUDS = {
  [VAULT_TYPES.aws]: awsCloud,
  [VAULT_TYPES.gcp]: gcpCloud,
} as const satisfies Readonly<Record<VaultType, CloudDispatch>>;

/** The cloud of the vault of type `type`. Without one, as on the new item's
 * form before the customer picks one, that's the vault element's default's. */
export const toVaultCloud = (type: VaultType | undefined): CloudDispatch => {
  const vaultType = type ?? RepositoryAccessVault.default;
  return isVaultType(vaultType) ? VAULT_CLOUDS[vaultType] : awsCloud;
};

/** Whether `type` is a vault type P0 knows. The element's default is typed as
 * any string. */
const isVaultType = (type: string): type is VaultType =>
  Object.hasOwn(VAULT_CLOUDS, type);
